import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MilestoneLayout from "./MilestoneLayout";

/* -------------------- Styles -------------------- */

const bgPalette = [
  "rgba(251, 234, 200, 1)",
  "rgba(192, 229, 232, 1)",
  "rgba(244, 204, 217, 1)",
];

const styles = {
  container: {
    width: "100%",
    maxWidth: "100%",
    ml: { xs: 0, sm: "10px" },
    overflowX: "hidden",
    boxSizing: "border-box",
  },
  title: {
    textAlign: "center",
    fontWeight: 600,
    fontSize: { xs: "16px", sm: "18px" },
    mb: 3,
  },
  cardWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: { xs: 2, sm: 4 },
    flexWrap: "wrap",
    width: "100%",
    maxWidth: "100%",
  },
  cardOuter: (active) => ({
    width: { xs: "100%", sm: 280 },
    maxWidth: { xs: 320, sm: 280 },
    position: "relative",
    cursor: "pointer",
    border: 0,
    background: "transparent",
    p: 0,
    mt: 4,
    font: "inherit",
    color: "inherit",
    textAlign: "center",
    transition: "transform 180ms ease",
    transform: active ? "translateY(-4px)" : "none",
    "&:hover": {
      transform: active ? "translateY(-5px)" : "translateY(-3px)",
    },
    "&:focus-visible": {
      outline: "3px solid rgba(247, 170, 71, 0.72)",
      outlineOffset: 5,
      borderRadius: "26px",
    },
  }),

  colorBg: (item, active) => ({
    height: 320,
    borderTopRightRadius: "40%",
    borderBottomLeftRadius: "40%",
    backgroundColor: item.bg,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-end",
    pb: 3,
    boxShadow: active
      ? "0 0 0 3px rgba(247, 170, 71, 0.46), 0 18px 34px rgba(41, 46, 92, 0.18)"
      : "0 10px 24px rgba(41, 46, 92, 0.08)",
    transition: "box-shadow 180ms ease",
  }),

  whiteCard: (active) => ({
    width: "85%",
    backgroundColor: "#fff",
    boxShadow: active
      ? "0px 12px 28px rgba(41, 46, 92, 0.16)"
      : "0px 8px 24px rgba(0,0,0,0.08)",
    textAlign: "center",
    p: 2,
    position: "absolute",
    top: 59,
    height: 290,
    borderRadius: "20%",
    borderTopRightRadius: 0,
    border: active
      ? "1px solid rgba(247, 170, 71, 0.52)"
      : "1px solid transparent",
    boxSizing: "border-box",
    transition: "border-color 180ms ease, box-shadow 180ms ease",
  }),

  selectionIndicator: (active) => ({
    position: "absolute",
    top: 13,
    right: 14,
    zIndex: 2,
    minWidth: active ? 86 : 24,
    height: 25,
    px: active ? 0.9 : 0,
    borderRadius: 999,
    backgroundColor: active
      ? "rgba(255, 255, 255, 0.88)"
      : "rgba(255, 255, 255, 0.72)",
    border: `1px solid ${
      active ? "rgba(247, 170, 71, 0.82)" : "rgba(226, 236, 243, 0.9)"
    }`,
    color: active ? "#292E5C" : "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
    boxShadow: active
      ? "0 8px 18px rgba(247, 170, 71, 0.24)"
      : "0 4px 10px rgba(41, 46, 92, 0.08)",
    transition:
      "min-width 180ms ease, background-color 180ms ease, border-color 180ms ease, color 180ms ease, box-shadow 180ms ease",
    "& .MuiSvgIcon-root": {
      fontSize: 14,
      color: active ? "#F7AA47" : "#B8C1D1",
    },
  }),

  optionLabel: {
    position: "absolute",
    top: "20px",
    fontSize: 13,
    fontStyle: "normal",
    fontWeight: 400,
    color: "#231F20",
  },
  optionLabelText: (active) => ({
    fontSize: "14px",
    fontWeight: 700,
    color: active ? "#292E5C" : "#2B2828",
    letterSpacing: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 27,
    px: active ? 1.35 : 0,
    borderRadius: 999,
    backgroundColor: active ? "rgba(247, 170, 71, 0.24)" : "transparent",
    border: active
      ? "1px solid rgba(247, 170, 71, 0.62)"
      : "1px solid transparent",
    boxShadow: active ? "0 7px 16px rgba(247, 170, 71, 0.2)" : "none",
    transition:
      "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, color 180ms ease, transform 180ms ease",
    transform: active ? "translateY(-1px)" : "none",
  }),

  image: { width: 90, height: 79, mb: 2 },
  cardTitle: {
    display: "block",
    fontSize: "15px",
    color: "#231F20",
    fontWeight: 600,
    mb: 2,
  },
  description: {
    display: "block",
    fontSize: "14px",
    color: "#6B7280",
    fontWeight: 400,
    lineHeight: 1.6,
  },
  subtitle: { textAlign: "center", mt: 2, fontSize: "12px", color: "#6B7280" },
};

/* -------------------- Component -------------------- */

const RecommendedCareer = ({ showMilestoneLayout = true, career = {} }) => {
  const [careerOptions, setCareerOptions] = useState([]);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [loading, setLoading] = useState(false);
  const formatLabel = (value = "") => {
    if (!value) return "-";

    return value
      .split(",")
      .map(item =>
        item
          .trim()
          .replace(/_/g, " ")
      )
      .join(", ");
  };
  const fetchCareerSummary = useCallback(async () => {
    try {
      setLoading(true);

      const res = { data: { career_info: career } };
      const careerList = Array.isArray(res?.data?.career_info)
        ? res.data.career_info
        : [];

      const mappedData = careerList.map((item, index) => {
        const priority = Number(item?.priority);
        const optionNumber =
          item?.priority !== undefined &&
          item?.priority !== null &&
          item?.priority !== "" &&
          Number.isFinite(priority)
            ? priority + 1
            : index + 1;

        return {
          id: index + 1,
          title: String(item?.career_choice || `Career ${optionNumber}`).replace(
            /_/g,
            " "
          ),
          subtitle: `CAREER ${optionNumber}`,
          bg: bgPalette[index % bgPalette.length],
          description: item?.short_description,
          raw: item, // keep original if needed later
        };
      });

      setCareerOptions(mappedData);
      setSelectedCareer(mappedData[0] || null);
    } catch (error) {
      console.log("error", error);
      setCareerOptions([]);
      setSelectedCareer(null);
    } finally {
      setLoading(false);
    }
  }, [career]);

  useEffect(() => {
    fetchCareerSummary();
  }, [fetchCareerSummary]);

  if (loading) return null;

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>
        Career Options Recommended
      </Typography>

      <Box sx={styles.cardWrapper}>
        {careerOptions.map((item) => {
          const isActive = selectedCareer?.id === item.id;

          return (
            <Box
              component="button"
              key={item.id}
              type="button"
              aria-pressed={isActive}
              sx={styles.cardOuter(isActive)}
              onClick={() => setSelectedCareer(item)}
            >
              <Box component="span" sx={styles.colorBg(item, isActive)}>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={styles.selectionIndicator(isActive)}
                >
                  <CheckCircleIcon />
                  {isActive ? "Selected" : ""}
                </Box>

                <Box component="span" sx={styles.optionLabel}>
                  <Typography
                    component="span"
                    sx={styles.optionLabelText(isActive)}
                  >
                    {formatLabel(item.subtitle)}
                  </Typography>
                </Box>

                <Box component="span" sx={styles.whiteCard(isActive)}>
                  {/* <Box
                    component="img"
                    src={`/assets/newImages/${item.title.replace(" ", "_")}.png`}
                    alt={item.title}
                    sx={styles.image}
                  /> */}

                  <Typography component="span" sx={styles.cardTitle}>
                    {formatLabel(item.title)}
                  </Typography>

                  <Typography component="span" sx={styles.description}>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {selectedCareer && showMilestoneLayout && (
        <MilestoneLayout selectedCareer={selectedCareer} />
      )}
    </Box>
  );
};

RecommendedCareer.propTypes = {
  showMilestoneLayout: PropTypes.bool,
  career: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
};

export default RecommendedCareer;
